export const Blogs = [
  {
    id: 1,
    name: "My Work on Blog Design",
    tags: ["React", "SASS", "UI Design"],
    date: "October 3, 2025",
    imgSrc:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ32TQyWJ0OM17k2WKjhzaz9vPyphhtx6V23w&usqp=CAU",
    link: "",
    summary:
      "In this post, I share my journey designing a personal blog from scratch — the challenges, the technologies I chose, and the lessons learned along the way. From late-night coding sessions to that final 'aha!' moment, it was totally worth it.",
    description: `<div class="article-content">

      <p>Hey there! Today I want to share one of my favorite projects with you. I worked on a blog design, and trust me, this journey was both challenging and incredibly fun. Those late nights coding while sipping my coffee... But the result? Totally worth it!</p>

      <h3>So Why Did I Design a Blog?</h3>
      <p>It all started with a simple question: <em>"What would a blog I'd actually enjoy using look like?"</em> I looked at templates out there, and most were either too complicated or too boring. I wanted something both stylish and easy to use. So I told myself: "You got this!"</p>

      <h3>Technologies I Used</h3>
      <p>In this project, I brought together modern web technologies:</p>
      <ul>
        <li><strong>React:</strong> Thanks to its component-based structure, I kept everything modular. Once I build a card component, I can use it everywhere!</li>
        <li><strong>SASS:</strong> Writing CSS has never been this fun. Variables, nested styles... It's like magic!</li>
        <li><strong>React Router:</strong> Page transitions became smooth as butter.</li>
        <li><strong>Framer Motion:</strong> Small animations make a big difference, I learned that.</li>
      </ul>

      <h3>How Did the Design Process Go?</h3>
      <p>First, I grabbed a pen and paper and sketched out my ideas. Yes, sometimes the best ideas are born in analog, not digital! Then I transferred these sketches to Figma and refined them.</p>
      <p>Things I focused on in the design:</p>
      <ul>
        <li>Clean and minimal look - easy on the eyes</li>
        <li>Readable typography - because blog means reading</li>
        <li>Mobile responsiveness - everyone browses on their phone nowadays</li>
        <li>Fast loading - nobody wants to wait, including me!</li>
      </ul>

      <h3>The Hardest Part</h3>
      <p>Let me be honest: Responsive design gave me a hard time. Something that looks amazing on desktop can be a disaster on mobile. I spent hours with CSS Grid and Flexbox, but finally got a design that looks great on every screen size. I can't describe the joy I felt at that moment!</p>

      <h3>What I Learned</h3>
      <p>This project taught me so much:</p>
      <ul>
        <li>Patience - sometimes fixing a bug takes hours</li>
        <li>Attention to detail - small things make a big difference</li>
        <li>User-centered thinking - it's not about me, it's about the user</li>
        <li>Never give up - every problem has a solution</li>
      </ul>

      <h3>Final Words</h3>
      <p>I had fun and grew as a developer while working on this project. If you want to build something too, just start! You don't have to be perfect, you just have to begin. In the future, I'm planning to integrate a CMS into this design and add more features.</p>
      <p>Feel free to reach out if you have any questions. See you in the next post!</p>
    </div>`,
  },
  {
    id: 2,
    name: "Developing My Personal Portfolio Page",
    tags: ["HTML", "CSS", "JavaScript"],
    date: "September 22, 2025",
    imgSrc:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ32TQyWJ0OM17k2WKjhzaz9vPyphhtx6V23w&usqp=CAU",
    link: "",
    summary:
      "From satellite systems to live casino operations to React development — discover how a non-traditional background shaped a unique approach to building a personal portfolio site with modern tools and smooth animations.",
    description: `<div class="article-content">

  <p>
    What if I told you that someone who worked with satellites, dealt cards in live casinos, and explored the world of network marketing ended up building a React portfolio? Sounds like a plot twist, right? Well, that's exactly my story. Grab a coffee — this one's interesting.
  </p>

  <h3>The Unexpected Beginning</h3>
  <p>
    Ten years ago, if you'd told me I'd be learning JavaScript and building web applications with React, I probably would have laughed it off. My journey started with satellite technologies — I've always had a special fascination with electronic structures.
  </p>
  <p>
    Over time, life pulled me into the fast-paced world of live casino operations, where every second is critical and precision means everything. Then came my encounter with multi-level marketing. Although that chapter ended in collapse, given my inexperience at the time, it taught me invaluable lessons about human relationships, communication, and the true meaning of persistence.
  </p>
  <p>
    Here's the real surprise: all these different experiences were unknowingly preparing me for a field I never planned to enter — coding.
  </p>

  <h3>Why React? Why Now?</h3>
  <p>
    When I decided to build my portfolio, I didn't want just a static page. I wanted something alive, something that moves, breathes, and tells a story. That's when I discovered <strong>React</strong>. Component-based thinking? That reminded me of satellite systems — modular, reusable, efficient. It just clicked.
  </p>

  <h3>The Tech Stack Behind This Site</h3>
  <p>Here's what powers this portfolio:</p>
  <ul>
    <li><strong>React 17.0.2:</strong> The heart of everything. Every section you see is a carefully crafted component.</li>
    <li><strong>React Router DOM 5.2.0:</strong> Seamless navigation between pages — Home, About, Blog, Work, Skills — without page reloads. That smooth SPA experience!</li>
    <li><strong>Styled Components 5.3.0:</strong> CSS-in-JS magic. Each component carries its own styles. No more messy CSS files!</li>
    <li><strong>Framer Motion 4.1.17:</strong> Those smooth animations you see? Page transitions, hover effects, the floating spaceman — all Framer Motion.</li>
  </ul>

  <h3>The Little Details That Matter</h3>
  <p>But wait, there's more under the hood:</p>
  <ul>
    <li><strong>TSParticles:</strong> That mesmerizing particle background? It's not just decoration — it represents the chaos of experiences that eventually formed a clear path.</li>
    <li><strong>normalize.css:</strong> Because browsers are weird, and consistency matters.</li>
    <li><strong>Light/Dark Themes:</strong> Different moods, different themes. Your choice.</li>
    <li><strong>web-vitals:</strong> I track performance because speed matters. Nobody waits anymore.</li>
  </ul>

  <h3>The Architecture</h3>
  <p>
    This isn't just a single page — it's an entire ecosystem:
  </p>
  <ul>
    <li><strong>Dynamic Blog System:</strong> BlogData.js feeds all the posts you're reading. Easy to update, easy to scale.</li>
    <li><strong>Work Gallery:</strong> WorkData.js showcases my projects. Add a new project? Just update the data file.</li>
    <li><strong>Reusable Components:</strong> Cards, titles, social icons, logo — built once, used everywhere.</li>
    <li><strong>Animated UI:</strong> Every interaction has feedback. Hover, click, scroll — it all responds.</li>
  </ul>

  <h3>What My Past Taught Me About Code</h3>
  <p>
    Here's the surprising part: my diverse background actually made me a better developer. Satellite systems taught me to think in modules. Casino operations taught me that timing and user experience are everything. MLM taught me that presentation matters. Now, when I write code, I'm not just writing functions — I'm crafting experiences.
  </p>

  <h3>The Plot Twist You Didn't Expect</h3>
  <p>
    Remember when I said I believe anything crafted with heart and mind becomes art? This portfolio is my proof. It's not just a website — it's every late night of learning, every failed attempt, every "aha!" moment compiled into one place. The real twist? I'm just getting started.
  </p>

  <p>
    So, what do you think? Did a casino dealer turned developer surprise you? Feel free to explore, connect with me through my social links, and stay tuned — the next chapter is being written right now.
  </p>
</div>
`,
  },
  {
    id: 3,
    name: "Minimalist E-Commerce Interface Design",
    tags: ["Shopify", "Figma", "Design Thinking"],
    date: "November 18, 2025",
    imgSrc:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ32TQyWJ0OM17k2WKjhzaz9vPyphhtx6V23w&usqp=CAU",
    link: "",
    summary:
      "A deep dive into designing a minimalist e-commerce interface using Figma and Shopify, applying Design Thinking principles at every stage — from user research and wireframes all the way to a high-fidelity, developer-ready prototype.",
    description: `<div class="article-content">
  <h2>Minimalist E-Commerce Interface Design</h2>
  <p><strong>Date:</strong> April 28, 2026</p>
  <p><strong>Tags:</strong> Shopify, Figma, Design Thinking</p>
  <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ32TQyWJ0OM17k2WKjhzaz9vPyphhtx6V23w&usqp=CAU" alt="Minimalist E-Commerce Interface" style="max-width:100%; height:auto; margin: 20px 0;" />

  <p>
    In this project, I designed a minimalist e-commerce interface aimed at enhancing product visibility and streamlining the shopping experience. This project was a deep dive into the intersection of user psychology, visual hierarchy, and practical e-commerce functionality. Using <strong>Figma</strong> for prototyping and <strong>Shopify</strong> as the target platform, I implemented <strong>Design Thinking</strong> principles throughout the entire process.
  </p>

  <h3>Project Objectives</h3>
  <p>
    The main objective was to create a design that simplifies the user journey from homepage to checkout while maintaining a premium aesthetic. The target audience consisted of modern consumers who value clean design, intuitive navigation, and responsive interfaces across all devices.
  </p>

  <h3>Design Process</h3>
  <ol>
    <li><strong>Empathize:</strong> Conducted user interviews and competitor analysis to identify pain points and opportunities.</li>
    <li><strong>Define:</strong> Created user personas and mapped user journeys to understand behavior patterns.</li>
    <li><strong>Ideate:</strong> Sketched out multiple wireframe concepts focused on minimalism, clarity, and conversion.</li>
    <li><strong>Prototype:</strong> Designed high-fidelity mockups using Figma with real product images, grid layouts, and consistent spacing.</li>
    <li><strong>Test:</strong> Shared prototypes with users for feedback, which led to refinements in CTA positioning and product filtering flow.</li>
  </ol>

  <h3>Design Highlights</h3>
  <ul>
    <li>A large hero banner with edge-to-edge product imagery to instantly capture attention.</li>
    <li>Simplified navigation bar with essential links: Shop, Categories, Cart, and Account.</li>
    <li>Sticky add-to-cart button on product pages for better mobile usability.</li>
    <li>Product filters and sorting features with accordion design on mobile.</li>
    <li>Minimal color palette (black, white, and one accent color) to emphasize content.</li>
  </ul>

  <h3>Tools & Deliverables</h3>
  <p>
    All design assets were created in Figma with organized components, auto layout settings, and proper use of style libraries. I also exported developer-ready assets and provided documentation for seamless handoff to the development team working with Shopify.
  </p>

  <h3>Outcomes</h3>
  <p>
    The final design received positive feedback from both stakeholders and test users. It successfully balanced aesthetic minimalism with functionality. Conversion-focused elements like trust badges, quick checkout options, and responsive image galleries were integrated without compromising simplicity.
  </p>

  <h3>What I Learned</h3>
  <p>
    This project taught me the importance of design restraint—knowing when to remove elements rather than add more. It also reinforced the power of iteration in the design thinking cycle and the value of direct user input. Working with Shopify’s limitations and strengths gave me practical insight into designing within a CMS framework.
  </p>

  <p>
    I plan to expand this project by building a live Shopify theme based on this design and testing real-world analytics such as click-through and drop-off rates to further optimize performance.
  </p>
</div>
`,
  },
];
